import { Injectable } from '@angular/core';
import { ServiceBase } from '../../core/service/service.base';
import { AuthService } from '../../core/service/auth.service';
import { LogService } from '../../core/service/log.service';
import { CustomErrorHandler } from '../../core/service/CustomErrorHandler';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';

interface ExtCircPayload {
  id: string;
  [key: string]: unknown;
}

interface NotSubmittedRevalidationPayload {
  renewalDate: string | Date;
}

@Injectable()
export class ExtCircService extends ServiceBase {

  constructor(http: HttpClient, auth: AuthService, log: LogService, errorHandler: CustomErrorHandler) {
    super(http, auth, log, errorHandler);
  }

  getAll() {
    
    return this.get('v1.0/user/extenuatingcircumstances');
  }

  getOne(id: string) {
    return this.get(`v1.0/user/extenuatingcircumstances/${id}`);
  }



  download(url: string) {
    return this.getFile(url);
  }

  assignItem(id: string, userId: string) {
    return this.post(`v1.0/user/extenuatingcircumstances/${id}/assign`,
      {
        ObjectId: userId
      });
  }

  // legacy
  reject(id: string, reason: string) {
    return this.post(`v1.0/user/extenuatingcircumstances/${id}/reject`, { reason });
  }
  // legacy
  approve(id: string, reason: string) {
    return this.post(`v1.0/user/extenuatingcircumstances/${id}/approve`, { reason });
  }

  saveRecommendation(payload: ExtCircPayload) {
    return this.post(`v1.0/user/extenuatingcircumstances/${payload.id}/review`, payload);
  }

  saveDecision(payload: ExtCircPayload) {
    return this.post(`v1.0/user/extenuatingcircumstances/${payload.id}/decision`, payload);
  }

  submitDecision(id: string) {
    return this.post(`v1.0/user/extenuatingcircumstances/${id}/submit`, {});
  }


  search(regNumber: string) {
    return this.get(`v1.0/user/extenuatingcircumstances?registrationNumber=${regNumber}`);
  }

  filter(status: string | number) {
    return this.get(`v1.0/user/extenuatingcircumstances?status=${status}`);
  }


  getRemediationSummary() {
    return super.get(`v1.0/revalidation/getRegistrantSummary`);
  }


  getRegistrantsInRemediation(submissionDeadline: string, skip: number, take: number) {
    return super.get(`v1.0/revalidation/getRegistrantsInRemediation?SubmissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}`);
  }

  getRegistrantsInNoir(submissionDeadline: string, skip: number, take: number) {
    return super.get(`v1.0/revalidation/getRegistrantsInNoIR?submissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}`);
  }

  getRegistrantsInNor(submissionDeadline: string, skip: number, take: number) {
    return super.get(`v1.0/revalidation/getRegistrantsInNoR?submissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}`);
  }

  issueNoir(regNumber: string) {
    // TODO: implement call to API
    return of('');
  }

  moveRegistrantToNOR(revalidationId: string | number) {
    return super.post(`v1.0/admin/revalidation/updateStage?revalidationId=${revalidationId}&stage=4`, {});
  }

  moveRegistrantToNoir(revalidationId: string | number) {
    return super.post(`v1.0/admin/revalidation/updateStage?revalidationId=${revalidationId}&stage=3`, {});
  }

  issueSupplementaryNoir(revalidationId: string | number) {
    return super.post(`v1.0/admin/revalidation/issueSupplementaryNoIR/?revalidationId=${revalidationId}`, {});
  }

  issueNorLetter(revalidationId: string | number, date: string | Date) {
    return super.post(`v1.0/admin/revalidation/issueNoRLetter`, { revalidationId,  NoRLetterIssuedAt: date});
  }

  searchRegistrationNumberNor(submissionDeadline: string, regNumber: string, skip: number, take: number) {
    return super.get(`v1.0/revalidation/getRegistrantsInNoR/?SubmissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}&registrationNumber=${regNumber}`);
  }

  searchRegistrationNumberNoir(submissionDeadline: string, regNumber: string, skip: number, take: number) {
    return super.get(`v1.0/revalidation/getRegistrantsInNoIR/?SubmissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}&registrationNumber=${regNumber}`);
  }

  searchRegistrationNumberRemediation(submissionDeadline: string, regNumber: string, skip: number, take: number) {
    return super.get(`v1.0/revalidation/getRegistrantsInRemediation/?SubmissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}&registrationNumber=${regNumber}`);
  }

  filterByManuallyEditedNor(submissionDeadline: string, skip: number, take: number) {
    return super.get(`v1.0/revalidation/getRegistrantsInNoR/?SubmissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}&isManual=true`);
  }

  filterByManuallyEditedNir(submissionDeadline: string, skip: number, take: number) {
    return super.get(`v1.0/revalidation/getRegistrantsInNoIR/?SubmissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}&isManual=true`);
  }

  filterByManuallyEditedRemediation(submissionDeadline: string, skip: number, take: number) {
    return super.get(`v1.0/revalidation/getRegistrantsInRemediation/?SubmissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}&isManual=true`);
  }

  filterByNorIssued(submissionDeadline: string, skip: number, take: number) {
    return super.get(`v1.0/revalidation/getRegistrantsInNoR/?SubmissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}&isNoRIssued=true`);
  }

  filterByNOIRIssued(submissionDeadline: string, skip: number, take: number, supNirIssued: boolean) {
    return super.get(`v1.0/revalidation/getRegistrantsInNoIR?submissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}&WasNoIRSupplementaryIssued=${supNirIssued}`);
  }

  filterByNOIRIssuedDate(submissionDeadline: string, skip: number, take: number, deadline: string | Date) {
    return super.get(`v1.0/revalidation/getRegistrantsInNoIR?submissionDeadline=${submissionDeadline}&skip=${skip}&take=${take}&NoIRSupplementaryDeadline=${deadline}`);
  }

  //MAE-12: Endpoint: Granted extension for submission but not submitted revalidations

  notSubmittedRevalidation(payload: NotSubmittedRevalidationPayload): Observable<any> {
    return super.post('v1.0/admin/registrant/revalidations/notsubmitted', payload);
  }

}
